/**
 * DotSizeLegend Component
 *
 * A compact legend that communicates the scatter plots' dot-SIZE encoding: the
 * radius of each dot is proportional (by area) to the number of days the data
 * point covers. It draws two reference dots — the smallest and largest day span
 * on the page — at the exact radii the plots use, each with its day-count label,
 * so the size scale is self-explanatory.
 *
 * Rendered in the routine surveillance plots toolbar (RsDataPlots), alongside
 * the "Days covered" filter, so the size encoding sits next to the size filter.
 */

import { JSX } from "react"
import { dotRadiusForSizeValue, DOT_RADIUS_MAX } from "@/components/rs-data/ScatterPlot"

/** Props for the DotSizeLegend component. */
interface DotSizeLegendProps {
  /**
   * The smallest day span across all plotted points on the page — the lower
   * reference dot. When null (no point carries a day span) the legend renders
   * nothing, since there is no size encoding to explain.
   */
  minDaySpan: number | null
  /**
   * The largest day span across all plotted points on the page — the upper
   * reference dot and the top of the size scale's domain. Pass the same value
   * given to the plots (maxSizeValue) so the reference dots match the real dots.
   */
  maxDaySpan: number
  /**
   * The plots' radius multiplier (ScatterPlot's dotRadiusScale). Defaults to 1
   * to match the small-multiple plots, which use the base radii.
   */
  dotRadiusScale?: number
}

/**
 * Legend explaining the dot-size (day-span) encoding used by the plots.
 *
 * Draws the min and max day-span reference dots at their true rendered radii
 * (via the shared dotRadiusForSizeValue helper) with day-count labels. Renders
 * nothing when there is no usable range to show (no spans, or min === max, in
 * which case size conveys nothing).
 *
 * @param props - Component props.
 * @param props.minDaySpan - Smallest day span on the page (null when none).
 * @param props.maxDaySpan - Largest day span on the page.
 * @param props.dotRadiusScale - Radius multiplier matching the plots (default 1).
 * @returns The size legend, or null when there is nothing to explain.
 */
export default function DotSizeLegend({
  minDaySpan,
  maxDaySpan,
  dotRadiusScale = 1,
}: DotSizeLegendProps): JSX.Element | null {
  // No usable size range → nothing to explain. This mirrors when the plots
  // actually vary dot size (a single span value renders uniform dots).
  if (minDaySpan === null || maxDaySpan <= 0 || minDaySpan >= maxDaySpan) {
    return null
  }

  // Radii for the two reference dots, computed with the SAME scale the plots
  // use so the legend is pixel-accurate.
  const minRadius: number = dotRadiusForSizeValue(minDaySpan, maxDaySpan, dotRadiusScale)
  const maxRadius: number = dotRadiusForSizeValue(maxDaySpan, maxDaySpan, dotRadiusScale)

  // The largest possible radius bounds the drawing box height so the big dot is
  // never clipped, regardless of the multiplier.
  const largestRadius: number = DOT_RADIUS_MAX * (dotRadiusScale > 0 ? dotRadiusScale : 1)
  const diameter: number = largestRadius * 2

  /**
   * Render one reference dot (a circle sized to `radius`, vertically centred in
   * the box) with its day-count label beneath.
   *
   * @param radius - The dot's radius in the same viewBox px the plots use.
   * @param days - The day count this dot represents (for the label).
   * @returns The dot + label as a small flex column.
   */
  const renderReferenceDot = (radius: number, days: number): JSX.Element => (
    <div className="flex flex-col items-center gap-0.5">
      <div
        className="flex items-end justify-center"
        style={{ height: diameter, width: diameter }}
      >
        <span
          className="rounded-full bg-primary/60 border border-primary"
          style={{ height: radius * 2, width: radius * 2 }}
          aria-hidden="true"
        />
      </div>
      <span className="text-[10px] text-muted-foreground tabular-nums leading-none">
        {days.toLocaleString()}
      </span>
    </div>
  )

  return (
    <div
      className="flex items-center gap-2"
      role="img"
      aria-label={
        `Dot size shows the number of days each point covers, ` +
        `from ${minDaySpan.toLocaleString()} to ${maxDaySpan.toLocaleString()} days.`
      }
    >
      <span className="text-sm text-muted-foreground whitespace-nowrap">
        Dot size = days covered
      </span>
      <div className="flex items-end gap-2" aria-hidden="true">
        {renderReferenceDot(minRadius, minDaySpan)}
        {renderReferenceDot(maxRadius, maxDaySpan)}
      </div>
    </div>
  )
}
